import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import expenseReportsService from '../../services/expenseReportsService';
import { displayNameOf } from '../../utils/people';
import DateRangeFilter, { useDateRange } from './DateRangeFilter';
import { inRange } from './dateRange';
import ExpenseFormModal from './ExpenseFormModal';
import ExpenseHistory from './ExpenseHistory';
import { exportExpenses } from './expenseExport';
import ExportMenu from './ExportMenu';
import PurposeCell from './PurposeCell';
import ReceiptCell from './ReceiptCell';
import ReceiptsModal from './ReceiptsModal';
import { STATUS_META, errorMessage, fmtCents, fmtExpenseDate, latestNote, sumCents } from './helpers';

const SummaryCard = ({ label, cents, count, tone }) => (
  <div className="col-sm-4">
    <div className={`card border-${tone} h-100`} role="group" aria-label={label}>
      <div className="card-body py-3">
        <div className="text-muted small">{label}</div>
        <div className="fs-4 fw-semibold">{fmtCents(cents)}</div>
        <div className="text-muted small">
          {count} {count === 1 ? 'expense' : 'expenses'}
        </div>
      </div>
    </div>
  </div>
);

/** The signed-in employee's own expenses: submit, edit or withdraw while pending, track status. */
const MyExpenses = () => {
  const auth = useContext(AuthContext);
  const { selection, update: updateRange, range, error: rangeError } = useDateRange();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null = closed, {} = new, expense = edit
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [receiptsFor, setReceiptsFor] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setExpenses(await expenseReportsService.getMine());
    } catch (err) {
      setError(`Couldn't load your expenses: ${errorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const byStatus = (status) => expenses.filter((e) => e.status === status);
    return {
      pending: byStatus('pending'),
      approved: byStatus('approved'),
      paid: byStatus('paid'),
    };
  }, [expenses]);

  // The cards above stay all-time; the table and the export follow the date range.
  const shown = useMemo(() => expenses.filter((e) => inRange(e, range)), [expenses, range]);
  const hidden = expenses.length - shown.length;

  const handleExport = (format) => {
    const name = expenses[0]?.employee_name || displayNameOf(auth?.user) || 'Me';
    return exportExpenses(shown, {
      range,
      statusKey: 'all',
      statusLabel: 'All statuses',
      employeeLabel: name,
      preparedBy: displayNameOf(auth?.user) || name,
    }, format);
  };

  const closeForm = () => {
    setEditing(null);
    setFormError('');
  };

  const handleSave = async (values, receiptChange) => {
    try {
      setSaving(true);
      setFormError('');
      if (editing && editing.expense_id) {
        await expenseReportsService.update(editing.expense_id, values);
        if (receiptChange) {
          try {
            await expenseReportsService.changeMyReceipts(editing.expense_id, receiptChange);
          } catch (err) {
            // The other changes are already saved; say so rather than imply nothing was.
            closeForm();
            await load();
            setError(`Your changes were saved, but the receipts weren't updated: ${errorMessage(err)}`);
            return;
          }
        }
      } else {
        await expenseReportsService.submit(values);
      }
      closeForm();
      await load();
    } catch (err) {
      setFormError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleWithdraw = async (expense) => {
    const label = `${fmtCents(expense.amount_cents)} at ${expense.merchant}`;
    if (!window.confirm(`Withdraw the expense for ${label}? This can't be undone.`)) return;
    try {
      setError('');
      await expenseReportsService.withdraw(expense.expense_id);
      await load();
    } catch (err) {
      await load();
      setError(`Couldn't withdraw the expense: ${errorMessage(err)}`);
    }
  };

  return (
    <>
      <div className="text-muted small mb-2">All dates</div>
      <div className="row g-3 mb-4">
        <SummaryCard label="Waiting for review" cents={sumCents(summary.pending)} count={summary.pending.length} tone="warning" />
        <SummaryCard label="Approved, to be paid back" cents={sumCents(summary.approved)} count={summary.approved.length} tone="primary" />
        <SummaryCard label="Paid back" cents={sumCents(summary.paid)} count={summary.paid.length} tone="success" />
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
            <h5 className="card-title mb-0">
              <i className="bi bi-receipt me-2"></i>
              My expenses ({shown.length})
            </h5>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <DateRangeFilter selection={selection} onChange={updateRange} error={rangeError} idPrefix="myRange" />
              <ExportMenu onExport={handleExport} disabled={loading || shown.length === 0} />
              <button className="btn btn-primary btn-sm" onClick={() => setEditing({})} disabled={loading}>
                <i className="bi bi-plus-circle me-2"></i>
                New expense
              </button>
            </div>
          </div>
          {hidden > 0 && (
            <div className="small text-muted mt-2" role="status">
              {hidden} of your {hidden === 1 ? 'expense falls' : 'expenses fall'} outside this date range.{' '}
              <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => updateRange({ key: 'all' })}>
                Show all dates
              </button>
            </div>
          )}
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : expenses.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-inbox display-4 d-block mb-3"></i>
              No expenses yet. Click <strong>New expense</strong> to submit one.
            </div>
          ) : shown.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-calendar-x display-4 d-block mb-3"></i>
              None of your expenses fall in this date range.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Vendor</th>
                    <th>Business purpose</th>
                    <th className="text-end">Amount</th>
                    <th>Status</th>
                    <th>Receipts</th>
                    <th>Latest note</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((expense) => {
                    const note = latestNote(expense);
                    const meta = STATUS_META[expense.status] || { label: expense.status, badge: 'text-bg-secondary' };
                    const isOpen = expanded === expense.expense_id;
                    return (
                      <Fragment key={expense.expense_id}>
                        <tr>
                          <td className="text-nowrap">{fmtExpenseDate(expense.expense_date)}</td>
                          <td>{expense.category}</td>
                          <td>{expense.merchant}</td>
                          <PurposeCell expense={expense} />
                          <td className="text-end text-nowrap">{fmtCents(expense.amount_cents)}</td>
                          <td><span className={`badge ${meta.badge}`}>{meta.label}</span></td>
                          <ReceiptCell expense={expense} />
                          <td className="small" style={{ maxWidth: 240 }}>
                            {note ? (
                              <>
                                <span className="fst-italic">“{note.note}”</span>
                                <div className="text-muted">{note.by_name}</div>
                              </>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="text-end text-nowrap">
                            <button
                              className="btn btn-link btn-sm"
                              onClick={() => setExpanded(isOpen ? null : expense.expense_id)}
                              aria-label={isOpen ? 'Hide history' : 'Show history'}
                              title="History"
                            >
                              <i className="bi bi-clock-history"></i>
                            </button>
                            {expense.status === 'pending' && (
                              <>
                                <button className="btn btn-outline-secondary btn-sm me-1" onClick={() => setEditing(expense)}>
                                  Edit
                                </button>
                                <button className="btn btn-outline-danger btn-sm" onClick={() => handleWithdraw(expense)}>
                                  Withdraw
                                </button>
                              </>
                            )}
                            {/* Pending expenses change receipts through Edit. Once reviewed the
                                fields lock, but receipts can change until paid (and be viewed after). */}
                            {expense.status !== 'pending' && (
                              <button
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setReceiptsFor(expense)}
                                aria-label={`Receipts for ${expense.merchant}`}
                              >
                                <i className="bi bi-paperclip me-1" aria-hidden="true"></i>
                                Receipts
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr className="table-light">
                            <td colSpan={9}>
                              <ExpenseHistory history={expense.history} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {receiptsFor && (
        <ReceiptsModal
          expense={receiptsFor}
          mode="mine"
          onClose={() => setReceiptsFor(null)}
          onSaved={(updated) => {
            setExpenses((prev) => prev.map((e) => (e.expense_id === updated.expense_id ? updated : e)));
            setReceiptsFor(null);
          }}
        />
      )}

      {editing && (
        <ExpenseFormModal
          expense={editing.expense_id ? editing : null}
          onSave={handleSave}
          onClose={closeForm}
          saving={saving}
          error={formError}
        />
      )}
    </>
  );
};

export default MyExpenses;
